import { ChangeEvent as ReactChangeEvent, FC, useCallback, useEffect, useMemo, useState } from 'react';
import {
  RemoteProcedureCall,
  useApplicationStateLoader,
  useApplicationStateValue,
} from '@resistdesign/voltra/app/utils';
import { APP_ENV_CONFIG } from '../config';
import { PROFILE } from './Profile/IDs';
import { Profile as ProfileType } from '../../common/Types';
import {
  Center,
  Controls,
  Footer,
  FooterControls,
  Header,
  Layout,
  Left,
  MainLayout,
  Right,
} from '../Components/Layout';
import { uploadFileToS3SignedUrl } from '../Utils/FileUtils';
import styled from 'styled-components';

const {
  api: { domain: API_DOMAIN },
} = APP_ENV_CONFIG;

const ProfileContainer = styled.div<{ modified?: boolean }>`
  background-color: ${({ modified }) => (modified ? 'var(--pico-primary-focus)' : 'unset')};
  padding: 1em;
  border-radius: 0.5em;
`;

export const useProfileRPCs = ({ authorization }: { authorization: string }) => {
  const profileRPC = useMemo<RemoteProcedureCall>(
    () => ({
      serviceConfig: {
        protocol: 'https',
        domain: API_DOMAIN,
        basePath: '/user/profile',
        authorization,
      },
      path: '/read',
    }),
    [authorization]
  );
  const profileSaveRPC = useMemo<RemoteProcedureCall>(
    () => ({
      serviceConfig: {
        protocol: 'https',
        domain: API_DOMAIN,
        basePath: '/user/profile',
        authorization,
      },
      path: '/save',
    }),
    [authorization]
  );

  return { profileRPC, profileSaveRPC };
};

export type ProfileProps = {
  authorization: string;
};

export const Profile: FC<ProfileProps> = ({ authorization }) => {
  const { profileRPC, profileSaveRPC } = useProfileRPCs({ authorization });
  const {
    modified: profileModified,
    value: profile = {},
    onChange: onProfileChange,
  } = useApplicationStateValue(PROFILE.READ);
  const { complete, linkedInUrl, city }: ProfileType = profile;
  const { loading, invalidate: invalidateProfileInternal } = useApplicationStateLoader({
    identifier: PROFILE.READ,
    remoteProcedureCall: profileRPC,
  });
  const invalidateProfile = useCallback(() => {
    invalidateProfileInternal();
  }, [invalidateProfileInternal]);
  const { loading: saveLoading, makeRemoteProcedureCall: saveProfile } = useApplicationStateLoader({
    identifier: PROFILE.SAVE,
    remoteProcedureCall: profileSaveRPC,
    manual: true,
    onLoadComplete: invalidateProfile,
  });
  const onLinkedInInputChange = useCallback(
    ({ target: { value: newLinkedInUrl = '' } }: ReactChangeEvent<HTMLInputElement>) => {
      onProfileChange({
        ...profile,
        linkedInUrl: newLinkedInUrl,
      });
    },
    [profile, onProfileChange]
  );
  const onCityInputChange = useCallback(
    ({ target: { value: newCity = '' } }: ReactChangeEvent<HTMLInputElement>) => {
      onProfileChange({
        ...profile,
        city: newCity,
      });
    },
    [profile, onProfileChange]
  );
  const onBeginInterviewClick = useCallback(() => {
    history.pushState({}, '', '/interview');
  }, []);
  const resumeUploadUrlRPC = useMemo<RemoteProcedureCall>(
    () => ({
      serviceConfig: {
        protocol: 'https',
        domain: API_DOMAIN,
        basePath: '/user/file-urls',
        authorization,
      },
      path: '/upload-resume',
    }),
    [authorization]
  );
  const [resumeUploadFailed, setResumeUploadFailed] = useState(false);
  const [resumeFileIsUploading, setResumeFileIsUploading] = useState(false);
  const [resumeFile, setResumeFile] = useState<File | undefined>();
  const { value: resumeUploadUrl = '' } = useApplicationStateValue(PROFILE.RESUME_UPLOAD_URL);
  const { loading: resumeUploadUrlLoading, makeRemoteProcedureCall: getResumeUploadUrl } = useApplicationStateLoader({
    identifier: PROFILE.RESUME_UPLOAD_URL,
    remoteProcedureCall: resumeUploadUrlRPC,
    manual: true,
  });
  const uploadResume = useCallback(async () => {
    if (resumeFile && resumeUploadUrl) {
      setResumeFileIsUploading(true);
      setResumeUploadFailed(false);

      try {
        await uploadFileToS3SignedUrl(resumeUploadUrl, resumeFile);
      } catch (error) {
        setResumeUploadFailed(true);
      }

      invalidateProfile();
      setResumeFileIsUploading(false);
    }
  }, [resumeFile, resumeUploadUrl, invalidateProfile]);
  const onFileChosen = useCallback(
    ({ target: { files } }: ReactChangeEvent<HTMLInputElement>) => {
      if (files) {
        const file = files[0];

        if (file) {
          setResumeFile(file);
        }
      }
    },
    [profile, onProfileChange]
  );
  const isLoading = loading || saveLoading || resumeUploadUrlLoading || resumeFileIsUploading;

  useEffect(() => {
    if (profileModified) {
      const sTid = setTimeout(() => {
        saveProfile(profile);
      }, 1000);

      return () => clearTimeout(sTid);
    }
  }, [profileModified, profile, saveProfile]);

  useEffect(() => {
    if (resumeFile && !resumeUploadUrl) {
      getResumeUploadUrl();
    } else if (resumeFile && resumeUploadUrl) {
      uploadResume();
    }
  }, [resumeFile, resumeUploadUrl, getResumeUploadUrl, uploadResume]);

  return (
    <Layout>
      <Header>
        <h2>Profile</h2>
        <p>{complete ? '' : '(Please Complete)'}</p>
      </Header>
      <MainLayout>
        <Left />
        <Center>
          <ProfileContainer modified={profileModified}>
            {isLoading ? (
              <span aria-busy="true">Loading...</span>
            ) : (
              <>
                <label>
                  LinkedIn URL:
                  <input type="text" value={linkedInUrl} onChange={onLinkedInInputChange} />
                </label>
                <label>
                  City:
                  <input type="text" value={city} onChange={onCityInputChange} />
                </label>
                <Controls>
                  <label>
                    Upload Resume:
                    {resumeUploadFailed
                      ? ' (Failed: Please try again!)'
                      : complete
                      ? ' (Resume has been uploaded.)'
                      : ''}
                    <input type="file" onChange={onFileChosen} aria-invalid={resumeUploadFailed ? true : undefined} />
                  </label>
                </Controls>
              </>
            )}
          </ProfileContainer>
        </Center>
        <Right />
      </MainLayout>
      <Footer>
        <FooterControls>
          <button disabled={!complete || isLoading} onClick={onBeginInterviewClick}>
            Begin Interview
          </button>
        </FooterControls>
      </Footer>
    </Layout>
  );
};
